import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ProgressBar } from "react-bootstrap";
import { db } from "../../../../config/firebase";
import { doc, collection, setDoc, updateDoc, getDoc } from "firebase/firestore";
import { useBusinessAI } from "../../../../hooks/useBusinessAI";
import { stepConfigs } from "../../../../_start/partials/widgets/forms/stepConfig";

interface LocationState {
  userID: string;
  projectID: string;
  projectName: string;
  projectDescription: string;
}

interface StepProgress {
  stepNumber: number;
  title: string;
  status: "pending" | "processing" | "completed" | "error";
  data?: Record<string, string>;
}

const AIProgressPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectID, userID, projectName, projectDescription } = (location.state || {}) as LocationState;

  const { analyzeBusinessIdea, calculateScore, error: aiError } = useBusinessAI();

  const [currentStep, setCurrentStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);
  const [stepsProgress, setStepsProgress] = useState<StepProgress[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scoreCalculated, setScoreCalculated] = useState(false);

  // Initialize step progress list
  useEffect(() => {
    const steps: StepProgress[] = [];
    for (let i = 1; i <= 24; i++) {
      const config = stepConfigs[i];
      steps.push({
        stepNumber: i,
        title: config?.title || `Step ${i}`,
        status: "pending",
      });
    }
    setStepsProgress(steps);
  }, []);

  /**
   * Calculate progress percentage for a step based on filled fields
   */
  const calculateStepProgress = (formData: Record<string, unknown>): number => {
    const fields = Object.values(formData);
    if (fields.length === 0) return 0;
    const filledFields = fields.filter((v) => {
      if (typeof v === "string") {
        return v.trim().length > 0;
      }
      return v !== null && v !== undefined;
    }).length;
    return Math.round((filledFields / fields.length) * 100);
  };

  /**
   * Write a single step's data to Firestore
   */
  const writeStepData = async (
    stepKey: string,
    stepFormData: Record<string, string>
  ) => {
    const userRef = doc(db, "users", userID);
    const projectRef = doc(collection(userRef, "projects"), projectID);
    const progressRef = collection(projectRef, "progress");
    const stepDocRef = doc(progressRef, stepKey);

    await setDoc(stepDocRef, {
      form: stepFormData,
      stepProgress: calculateStepProgress(stepFormData),
      aiGenerated: true,
      updatedAt: new Date().toISOString(),
    });
  };

  /**
   * Update project's overall progress
   */
  const updateProjectProgress = async (completedSteps: number) => {
    const userRef = doc(db, "users", userID);
    const projectRef = doc(collection(userRef, "projects"), projectID);

    // Calculate overall progress (some initial progress + step completion)
    const progress = Math.round((completedSteps / 24) * 100);

    await updateDoc(projectRef, {
      projectProgress: progress,
      aiAnalysisComplete: completedSteps === 24,
      lastUpdated: new Date().toISOString(),
    });
  };

  /**
   * Run AI analysis and populate steps one by one
   */
  const runAnalysis = useCallback(async () => {
    if (!projectID || !userID || !projectDescription) {
      setError("Missing project information");
      return;
    }

    try {
      // Start analysis
      setCurrentStep(0);
      setOverallProgress(5);

      // Get AI-extracted data
      const extractedData = await analyzeBusinessIdea(projectDescription, projectName);

      if (!extractedData) {
        throw new Error(aiError || "Failed to analyze business idea");
      }

      setOverallProgress(15);

      // Process each step with visual feedback
      const stepKeys = Object.keys(extractedData).sort((a, b) => {
        const numA = parseInt(a.replace("step", ""), 10);
        const numB = parseInt(b.replace("step", ""), 10);
        return numA - numB;
      });

      let completedCount = 0;

      for (const stepKey of stepKeys) {
        const stepNum = parseInt(stepKey.replace("step", ""), 10);
        const stepFormData = extractedData[stepKey];

        // Skip if no data for this step
        if (!stepFormData) {
          continue;
        }

        // Update UI to show current step processing
        setCurrentStep(stepNum);
        setStepsProgress((prev) =>
          prev.map((s) =>
            s.stepNumber === stepNum ? { ...s, status: "processing" } : s
          )
        );

        // Small delay for visual effect
        await new Promise((resolve) => setTimeout(resolve, 150));

        // Write to Firestore
        await writeStepData(stepKey, stepFormData);

        // Mark step as complete
        completedCount++;
        setStepsProgress((prev) =>
          prev.map((s) =>
            s.stepNumber === stepNum
              ? { ...s, status: "completed", data: stepFormData }
              : s
          )
        );

        // Update progress
        const stepProgress = 15 + (completedCount / stepKeys.length) * 70;
        setOverallProgress(Math.round(stepProgress));

        // Update project progress in Firestore
        await updateProjectProgress(completedCount);
      }

      // Calculate business score
      setOverallProgress(90);
      try {
        await calculateScore(projectID);
        setScoreCalculated(true);
      } catch (scoreError) {
        console.warn("Score calculation failed:", scoreError);
        // Continue anyway - score is optional
      }

      setOverallProgress(100);
      setIsComplete(true);

    } catch (err) {
      console.error("Analysis failed:", err);
      setError(err instanceof Error ? err.message : "Analysis failed");
      setStepsProgress((prev) =>
        prev.map((s) =>
          s.status === "processing" ? { ...s, status: "error" } : s
        )
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectID, userID, projectDescription, projectName, analyzeBusinessIdea, calculateScore, aiError]);

  // Start analysis when component mounts
  useEffect(() => {
    if (projectID && userID && projectDescription && stepsProgress.length > 0) {
      runAnalysis();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsProgress.length]);

  const handleContinue = () => {
    navigate("/ai-review", {
      state: {
        projectID,
        userID,
        projectName,
        projectDescription,
      },
    });
  };

  const handleRetry = () => {
    setError(null);
    setOverallProgress(0);
    setCurrentStep(0);
    setStepsProgress((prev) =>
      prev.map((s) => ({ ...s, status: "pending" as const, data: undefined }))
    );
    runAnalysis();
  };

  // Redirect if no state
  if (!projectID || !userID) {
    return (
      <div className="card">
        <div className="card-body text-center py-15">
          <i className="bi bi-exclamation-triangle text-warning fs-2x mb-4"></i>
          <h3>Missing Project Information</h3>
          <p className="text-muted">Please create a project first.</p>
          <button
            className="btn btn-primary mt-4"
            onClick={() => navigate("/dashboard")}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="row justify-content-center">
      <div className="col-xl-10">
        {/* Header Card */}
        <div className="card mb-5">
          <div className="card-body">
            <div className="d-flex align-items-center">
              <div className="symbol symbol-60px me-5">
                <div className={`symbol-label ${isComplete ? "bg-light-success" : error ? "bg-light-danger" : "bg-light-primary"}`}>
                  {isComplete ? (
                    <i className="bi bi-check-lg text-success fs-2x"></i>
                  ) : error ? (
                    <i className="bi bi-exclamation-triangle text-danger fs-2x"></i>
                  ) : (
                    <i className="bi bi-magic text-primary fs-2x"></i>
                  )}
                </div>
              </div>
              <div className="flex-grow-1">
                <h2 className="fw-bolder mb-1">
                  {isComplete
                    ? "Analysis Complete!"
                    : error
                    ? "Analysis Failed"
                    : "Analyzing Your Business Idea"}
                </h2>
                <span className="text-muted fs-6">
                  {isComplete
                    ? "Your 24-step validation framework has been populated"
                    : error
                    ? error
                    : `AI is extracting data for ${projectName}`}
                </span>
              </div>
              {isComplete && (
                <button className="btn btn-primary btn-lg" onClick={handleContinue}>
                  <i className="bi bi-arrow-right me-2"></i>
                  View Project
                </button>
              )}
              {error && (
                <button className="btn btn-warning btn-lg" onClick={handleRetry}>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Retry
                </button>
              )}
            </div>

            {/* Overall Progress */}
            <div className="mt-8">
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-bold text-gray-700">Overall Progress</span>
                <span className="fw-bold text-primary">{overallProgress}%</span>
              </div>
              <ProgressBar
                now={overallProgress}
                animated={!isComplete && !error}
                striped={!isComplete && !error}
                variant={isComplete ? "success" : error ? "danger" : "primary"}
                className="h-15px"
              />
            </div>

            {/* Score Badge */}
            {isComplete && scoreCalculated && (
              <div className="mt-6">
                <div className="notice d-flex bg-light-success rounded border-success border border-dashed p-6">
                  <i className="bi bi-graph-up-arrow fs-2tx text-success me-4"></i>
                  <div className="d-flex flex-stack flex-grow-1">
                    <div className="fw-bold">
                      <h4 className="text-gray-800 fw-bolder">Business Score Calculated</h4>
                      <div className="fs-6 text-gray-600">
                        Your business strength score has been calculated based on the AI analysis.
                        View your detailed score breakdown on the project dashboard.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Steps Progress Grid */}
        <div className="card">
          <div className="card-header border-0">
            <h3 className="card-title fw-bolder text-dark">
              24-Step Validation Framework
            </h3>
            <div className="card-toolbar">
              <span className="badge badge-light-primary fs-7">
                {stepsProgress.filter((s) => s.status === "completed").length} / 24 completed
              </span>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="row g-4">
              {stepsProgress.map((step) => (
                <div key={step.stepNumber} className="col-md-6 col-lg-4">
                  <div
                    className={`d-flex align-items-center p-4 rounded ${
                      step.status === "completed"
                        ? "bg-light-success"
                        : step.status === "processing"
                        ? "bg-light-primary"
                        : step.status === "error"
                        ? "bg-light-danger"
                        : "bg-light"
                    }`}
                  >
                    {/* Status Icon */}
                    <div className="me-3">
                      {step.status === "completed" ? (
                        <i className="bi bi-check-circle-fill text-success fs-2"></i>
                      ) : step.status === "processing" ? (
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Processing...</span>
                        </div>
                      ) : step.status === "error" ? (
                        <i className="bi bi-x-circle-fill text-danger fs-2"></i>
                      ) : (
                        <i className="bi bi-circle text-gray-400 fs-2"></i>
                      )}
                    </div>

                    {/* Step Info */}
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-gray-400 fs-8">Step {step.stepNumber}</span>
                        {step.status === "completed" && step.data && (
                          <span className="badge badge-sm badge-light-success">
                            {Object.keys(step.data).length} fields
                          </span>
                        )}
                      </div>
                      <span
                        className={`fw-bold fs-7 ${
                          step.status === "completed"
                            ? "text-success"
                            : step.status === "processing"
                            ? "text-primary"
                            : step.status === "error"
                            ? "text-danger"
                            : "text-gray-600"
                        }`}
                      >
                        {step.title}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {isComplete && (
          <div className="d-flex justify-content-center mt-8">
            <button
              className="btn btn-light-primary btn-lg me-4"
              onClick={() => navigate("/dashboard")}
            >
              <i className="bi bi-house me-2"></i>
              Back to Dashboard
            </button>
            <button className="btn btn-primary btn-lg" onClick={handleContinue}>
              <i className="bi bi-arrow-right me-2"></i>
              View Project Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export { AIProgressPage };
